/**
 * Tracksy Travel Card — single-SKU secondary product.
 *
 * Standalone pricing (no bundle tiers). Prices mirror backend/pricing.py
 * `TRAVEL_CARD_PRICE` and must stay in sync. Free units awarded by 4-Pack
 * (+1 free) and 8-Pack (+1 free) purchases are computed server-side so this
 * file is only for the standalone product UI.
 */
export const TRAVEL_CARD = {
  id: "travel-card",
  slug: "travel-card",
  name: "Tracksy Travel Card",
  tagline: "The last tracker your wallet, luggage or keys will ever need.",
  priceByCurrency: { AUD: 39, USD: 29, GBP: 24 },
  compareAtByCurrency: { AUD: 59, USD: 44, GBP: 36 },
  rating: 4.9,
  reviewCount: 1812,
  heroImage:
    "/images/travel-card.webp",
  lifestyleImage:
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_tracksy-showcase/artifacts/37ab353339ee112b_Smart%20Tracker%20for%20Every%20Journey.png",
  // Short, punchy, benefit-led. Used on the home teaser.
  pillars: [
    { title: "Truly Universal", body: "The only card that talks to both 1.5 billion Androids and 1 billion iPhones worldwide." },
    { title: "The Last Tracker You'll Buy", body: "Rechargeable via any wireless pad in 70–80 minutes. No more battery replacements, ever." },
    { title: "Thinner Than Ever", body: "Just 2.0mm thick — slips into any credit card slot without a bulge." },
  ],
  // Deeper benefits for the product page.
  advantages: [
    { title: "Dual-Ecosystem Mastery", body: "Seamlessly switch between iOS and Android. Works natively with Apple Find My AND the latest Google Find Hub app." },
    { title: "The Invisible 2.0mm Profile", body: "20% thinner than standard trackers. Fits into any credit card slot without bulging your wallet." },
    { title: "Sustainably Powered", body: "No more battery replacements. Fully recharge in 70–80 minutes via any Qi-certified wireless charging pad." },
    { title: "Acoustic Precision", body: "If it's lost nearby, trigger a 75dB high-frequency sound to pinpoint its exact location instantly." },
    { title: "Global Community Search", body: "Leverage hundreds of millions of devices worldwide to locate your items anonymously, no matter where they are on the map." },
  ],
  useCases: [
    { label: "Wallets", sub: "All-Scene Anti-Lost", icon: "wallet" },
    { label: "Keys", sub: "Sound Localization", icon: "key" },
    { label: "Luggage", sub: "Remote Tracking", icon: "luggage" },
    { label: "Kids' Phones", sub: "Everyday Peace of Mind", icon: "phone" },
    { label: "Handbags", sub: "Travel-Ready", icon: "bag" },
    { label: "Backpacks", sub: "School & Commute", icon: "backpack" },
  ],
  setupApple: [
    { step: "Activate", body: "Press the button once to power on." },
    { step: "Pair", body: "Open Find My → Add New Item → Other Supported Item." },
    { step: "Complete", body: "Follow the prompts to name your tracker." },
  ],
  setupAndroid: [
    { step: "Install", body: "Download the Google Find Hub app from Google Play." },
    { step: "Pair", body: "Power on the card. Your phone will automatically trigger the connection prompt." },
    { step: "Sync", body: "Click Connect to integrate into your Google smart network." },
  ],
  specs: [
    { label: "Thickness", value: "2.0mm" },
    { label: "Weight", value: "16g" },
    { label: "Battery", value: "Rechargeable Li-Po · 70–80min Qi" },
    { label: "Signal Range", value: "80m line of sight" },
    { label: "Alarm", value: "75dB precision ping" },
    { label: "Compatibility", value: "Apple Find My + Google Find Hub" },
    { label: "Water Resistance", value: "IPX4 splash resistant" },
    { label: "Warranty", value: "12 months" },
  ],
  faqs: [
    { q: "Does one card really work on both iPhone and Android?", a: "Yes — same card, same hardware. Pair it with Apple Find My if you're on iPhone, or Google Find Hub if you're on Android. Switching phones later? Re-pair and it talks to the new ecosystem instantly." },
    { q: "How long does the battery last?", a: "Around 4–6 months of normal use between charges. When it dips, pop it on any Qi wireless pad for 70–80 minutes to fully recharge." },
    { q: "Why is it better than Spotminder or other card trackers?", a: "Most card trackers only speak one ecosystem's network — Apple OR Google. Tracksy Travel Card speaks BOTH, so you're backed by the full ~2.5 billion-device community search network globally. Thinner, too." },
    { q: "Does it fit in a slim wallet?", a: "Yes. At 2.0mm it's ~20% thinner than a standard tracker card and fits in any card slot without bulging the leather." },
    { q: "Is it water resistant?", a: "IPX4 — handles splashes, rain and sweat. Not for swimming." },
  ],
};
